<script setup lang="ts">
import { message } from 'ant-design-vue'
import { ref, watch } from 'vue'
import type { RoomAsset } from '@/models'
import { createRoomAsset, updateRoomAsset } from '@/services/roomAssets'

const props = defineProps<{
  visible: boolean
  asset: RoomAsset | null
  roomId: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'saved'): void
}>()

const saving = ref(false)
const formRef = ref()
const form = ref({
  itemName: '',
  quantity: 1,
  notes: '',
})

watch(
  () => props.visible,
  (val) => {
    if (val) {
      if (props.asset) {
        form.value = {
          itemName: props.asset.item_name,
          quantity: props.asset.quantity,
          notes: props.asset.notes || '',
        }
      } else {
        form.value = { itemName: '', quantity: 1, notes: '' }
      }
    }
  },
)

const handleSave = async () => {
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    if (props.asset) {
      await updateRoomAsset(props.asset.id, {
        item_name: form.value.itemName.trim(),
        quantity: form.value.quantity,
        notes: form.value.notes || undefined,
      })
      message.success('Asset updated')
    } else {
      await createRoomAsset({
        room_id: props.roomId,
        item_name: form.value.itemName.trim(),
        quantity: form.value.quantity,
        notes: form.value.notes || undefined,
      })
      message.success('Asset created')
    }
    emit('saved')
    emit('update:visible', false)
  } catch (e: any) {
    message.error(e?.message || 'Failed to save')
  } finally {
    saving.value = false
  }
}

const handleClose = () => {
  emit('update:visible', false)
}
</script>

<template>
  <a-modal
    :open="visible"
    :title="asset ? 'Edit Asset' : 'Add Asset'"
    @ok="handleSave"
    @cancel="handleClose"
    :confirm-loading="saving"
    ok-text="Save"
    width="450px"
  >
    <a-form ref="formRef" :model="form" layout="vertical">
      <a-form-item
        label="Item Name"
        name="itemName"
        :rules="[{ required: true, message: 'Item name is required' }]"
      >
        <a-input v-model:value="form.itemName" placeholder="e.g. Soap, Shampoo, Towels" />
      </a-form-item>
      <a-form-item label="Quantity" name="quantity">
        <a-input-number v-model:value="form.quantity" :min="1" style="width: 100%" />
      </a-form-item>
      <a-form-item label="Notes" name="notes">
        <a-textarea v-model:value="form.notes" :rows="2" placeholder="Optional notes" />
      </a-form-item>
    </a-form>
  </a-modal>
</template>
